import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Paperclip, Stamp } from 'lucide-react';
import { toast } from 'sonner';
import type { Acta, Asamblea, ResultadoMateriaEntity, RolFirma } from '@condo/contracts';
import { useActualizarActa, useFirmarActa, useSubirAdjuntoActa, useResultados } from './api';
import { ActaFirmarDialog } from './acta-firmar-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { formatLongDate, formatShortDate } from '@/lib/format/date';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const DEBOUNCE_MS = 1000;

const RESULTADO_CONFIG: Record<string, { label: string; variant: 'success' | 'danger' | 'warning' }> = {
  aprobada: { label: 'Aprobada', variant: 'success' },
  rechazada: { label: 'Rechazada', variant: 'danger' },
  sin_quorum: { label: 'Sin quorum', variant: 'warning' },
};

// ─────────────────────────────────────────────────────────────────────────────
// Tally preview (read-only)
// ─────────────────────────────────────────────────────────────────────────────

function TallyPreview({
  asamblea,
  resultados,
}: {
  asamblea: Asamblea;
  resultados: ResultadoMateriaEntity[];
}) {
  const byMateriaId = new Map(resultados.map((r) => [r.materiaId, r]));

  const votables = asamblea.materias.filter((m) => m.categoria !== 'informativa');

  if (votables.length === 0) {
    return (
      <p className="text-sm text-[var(--color-ink-muted)]">
        Esta asamblea no tuvo materias sujetas a votacion.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {votables.map((m, i) => {
        const r = byMateriaId.get(m.id);
        const config = r ? RESULTADO_CONFIG[r.resultado] : null;
        return (
          <div
            key={m.id}
            className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper-dim)] px-4 py-3"
          >
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm font-semibold text-[var(--color-ink)]">
                {i + 1}. {m.titulo}
              </p>
              {config ? (
                <Badge variant={config.variant} className="shrink-0 text-[0.6rem]">
                  {config.label}
                </Badge>
              ) : (
                <Badge variant="neutral" className="shrink-0 text-[0.6rem]">
                  Pendiente
                </Badge>
              )}
            </div>
            {r && (
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(r.conteoPorOpcion).map(([opcion, conteo]) => (
                  <span
                    key={opcion}
                    className="inline-flex items-center gap-1 rounded bg-[var(--color-paper)] border border-[var(--color-hairline)] px-2 py-0.5 text-xs text-[var(--color-ink-soft)]"
                  >
                    <span className="font-semibold capitalize">{opcion}</span>
                    <span className="text-[var(--color-ink-muted)]">
                      {conteo.cantidadVotos}v · {(conteo.alicuotaAcumulada * 100).toFixed(1)}%
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  condoId: string;
  asamblea: Asamblea;
  acta: Acta;
  /** session.userId to compare against asamblea.presidenteId */
  sessionUserId: string;
}

export function ActaEditor({ condoId, asamblea, acta, sessionUserId }: Props) {
  const asambleaId = asamblea.id;

  // ── API hooks ──────────────────────────────────────────────────────────────
  const actualizarMutation = useActualizarActa(condoId, asambleaId);
  const subirAdjuntoMutation = useSubirAdjuntoActa(condoId, asambleaId);
  const firmarMutation = useFirmarActa(condoId, asambleaId);
  const { data: resultadosData, isLoading: loadingResultados } = useResultados(
    condoId,
    asambleaId,
  );

  // ── Local state ────────────────────────────────────────────────────────────
  const [textoLibre, setTextoLibre] = useState(acta.textoLibre ?? '');
  const [firmarDialogRol, setFirmarDialogRol] = useState<RolFirma | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync if server state changes while editor is open (e.g. concurrent update)
  useEffect(() => {
    setTextoLibre(acta.textoLibre ?? '');
  }, [acta.textoLibre]);

  // ── Debounced save ────────────────────────────────────────────────────────
  const handleTextoChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setTextoLibre(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        actualizarMutation.mutate(value, {
          onError: () => toast.error('No se pudo guardar el texto del acta'),
        });
      }, DEBOUNCE_MS);
    },
    [actualizarMutation],
  );

  // ── Adjunto upload ────────────────────────────────────────────────────────
  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const { uploadUrl } = await subirAdjuntoMutation.mutateAsync({
        nombre: file.name,
        contentType: file.type || 'application/octet-stream',
      });

      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });

      if (!putRes.ok) throw new Error(`S3 PUT ${putRes.status}`);

      toast.success(`Adjunto "${file.name}" subido correctamente`);
    } catch {
      toast.error('No se pudo subir el adjunto');
    } finally {
      // Reset file input so the same file can be re-uploaded if needed
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  // ── Firma ─────────────────────────────────────────────────────────────────
  function handleFirmar(rol: RolFirma) {
    firmarMutation.mutate(
      { rol },
      {
        onSuccess: () => {
          toast.success(`Acta firmada como ${rol} (FEA mock)`);
          setFirmarDialogRol(null);
        },
        onError: () => toast.error('No se pudo firmar el acta'),
      },
    );
  }

  // ── Derived state ──────────────────────────────────────────────────────────
  const resultados = resultadosData?.items ?? [];
  const firmaAdmin = acta.firmas.find((f) => f.rol === 'administrador');
  const firmaPresidente = acta.firmas.find((f) => f.rol === 'presidente');

  // TODO: resolve propietarioId from session.userId via residente lookup —
  // for now we compare directly; will be incorrect until the backend maps
  // Cognito sub → propietarioId.
  const isPresidente = sessionUserId === asamblea.presidenteId;

  return (
    <div className="space-y-6">
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] px-6 py-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="eyebrow">Acta en edicion</p>
            <h2 className="font-display-tight mt-1 text-xl font-semibold">
              Asamblea {asamblea.tipo} — {formatLongDate(asamblea.fechaProgramada)}
            </h2>
            <p className="mt-0.5 text-sm text-[var(--color-ink-muted)]">{asamblea.lugar}</p>
          </div>
          <Badge variant="neutral">Borrador</Badge>
        </div>
      </div>

      {/* ── Two-column layout ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Left column: texto + adjuntos + tally */}
        <div className="space-y-6">
          {/* Texto libre */}
          <section className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-5">
            <div className="mb-3 flex items-center justify-between">
              <p className="eyebrow">Texto del acta</p>
              {actualizarMutation.isPending && (
                <span className="flex items-center gap-1 text-xs text-[var(--color-ink-muted)]">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Guardando…
                </span>
              )}
              {actualizarMutation.isSuccess && !actualizarMutation.isPending && (
                <span className="text-xs text-[var(--color-ink-muted)]">Guardado</span>
              )}
            </div>
            <textarea
              className="w-full rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper-dim)] p-3 text-sm text-[var(--color-ink)] placeholder-[var(--color-ink-faint)] resize-y min-h-[200px] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-1"
              placeholder="Redacta el texto del acta. Se guarda automaticamente…"
              value={textoLibre}
              onChange={handleTextoChange}
              rows={12}
            />
          </section>

          {/* Adjuntos */}
          <section className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-5">
            <div className="mb-3 flex items-center justify-between">
              <p className="eyebrow">Adjuntos</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={subirAdjuntoMutation.isPending}
              >
                {subirAdjuntoMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Paperclip className="h-4 w-4" />
                )}
                Subir adjunto
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => void handleFileChange(e)}
              />
            </div>

            {acta.adjuntos.length === 0 ? (
              <p className="text-sm text-[var(--color-ink-muted)]">
                No hay adjuntos. Sube documentos de respaldo para el acta.
              </p>
            ) : (
              <ul className="divide-y divide-[var(--color-hairline)]">
                {acta.adjuntos.map((a) => (
                  <li key={a.id} className="flex items-center gap-3 py-2">
                    <Paperclip className="h-3.5 w-3.5 shrink-0 text-[var(--color-ink-muted)]" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-[var(--color-ink)]">
                        {a.nombre}
                      </p>
                      <p className="text-xs text-[var(--color-ink-muted)]">
                        {a.contentType} · {formatShortDate(a.subidoEn)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Tally preview */}
          <section className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-5">
            <p className="eyebrow mb-3">Resultados de votacion (solo lectura)</p>
            {loadingResultados ? (
              <Skeleton className="h-24 w-full" />
            ) : (
              <TallyPreview asamblea={asamblea} resultados={resultados} />
            )}
          </section>
        </div>

        {/* Right column: PDF preview placeholder + firmas */}
        <div className="space-y-6">
          {/* PDF preview placeholder */}
          <section className="flex min-h-[320px] items-center justify-center rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper-dim)] p-8 text-center">
            <div>
              <p className="eyebrow">Vista previa PDF</p>
              <p className="mt-2 text-sm text-[var(--color-ink-muted)]">
                Preview disponible una vez firmada.
              </p>
              <p className="mt-1 text-xs text-[var(--color-ink-faint)]">
                (El endpoint de preview PDF esta pendiente de implementacion backend.)
              </p>
            </div>
          </section>

          {/* Firmas */}
          <section className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-5">
            <p className="eyebrow mb-4">Firmas electronicas</p>

            <div className="space-y-4">
              {/* Firma administrador */}
              <div className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper-dim)] p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
                      Administrador
                    </p>
                    {firmaAdmin ? (
                      <>
                        <p className="mt-1 font-mono text-xs text-[var(--color-ink-muted)] break-all">
                          {firmaAdmin.feaReferenceMock.slice(0, 40)}…
                        </p>
                        <p className="mt-0.5 text-xs text-[var(--color-ink-faint)]">
                          {formatShortDate(firmaAdmin.firmadoEn)}
                        </p>
                      </>
                    ) : (
                      <Badge variant="neutral" className="mt-1">
                        Pendiente
                      </Badge>
                    )}
                  </div>
                  {!firmaAdmin && (
                    <Button
                      size="sm"
                      onClick={() => setFirmarDialogRol('administrador')}
                      disabled={firmarMutation.isPending}
                    >
                      <Stamp className="h-3.5 w-3.5" />
                      Firmar
                    </Button>
                  )}
                </div>
              </div>

              {/* Firma presidente */}
              <div className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper-dim)] p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
                      Presidente de mesa
                    </p>
                    {firmaPresidente ? (
                      <>
                        <p className="mt-1 font-mono text-xs text-[var(--color-ink-muted)] break-all">
                          {firmaPresidente.feaReferenceMock.slice(0, 40)}…
                        </p>
                        <p className="mt-0.5 text-xs text-[var(--color-ink-faint)]">
                          {formatShortDate(firmaPresidente.firmadoEn)}
                        </p>
                      </>
                    ) : (
                      <Badge variant="neutral" className="mt-1">
                        Pendiente
                      </Badge>
                    )}
                  </div>
                  {!firmaPresidente && isPresidente && (
                    <Button
                      size="sm"
                      onClick={() => setFirmarDialogRol('presidente')}
                      disabled={firmarMutation.isPending}
                    >
                      <Stamp className="h-3.5 w-3.5" />
                      Firmar
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <p className="mt-4 text-[0.6875rem] text-[var(--color-ink-faint)]">
              FEA simulada — pendiente de integracion real con proveedor certificado.
            </p>
          </section>
        </div>
      </div>

      {/* Firmar dialog */}
      {firmarDialogRol && (
        <ActaFirmarDialog
          open={firmarDialogRol !== null}
          onOpenChange={(v) => { if (!v) setFirmarDialogRol(null); }}
          rol={firmarDialogRol}
          isPending={firmarMutation.isPending}
          onConfirm={() => handleFirmar(firmarDialogRol)}
        />
      )}
    </div>
  );
}
